import { useMutation, useQuery } from 'react-query';
import { APIError, APIResponse, API_URL_V1, get, post } from '../api';

// ── Types ──────────────────────────────────────────────────────────────────────

export type AdvanceStatus =
  | 'pending_hr'
  | 'hr_approved'
  | 'hr_declined'
  | 'pending_disbursement'
  | 'disbursed'
  | 'declined';

export interface RepaymentInstallment {
  installment_number: number;
  due_date: string;
  amount: number;
  status: 'pending' | 'paid';
}

export interface SalaryAdvanceRequest {
  id: string;
  reference: string;
  amount: number;
  reason: string | null;
  repayment_months: number;
  status: AdvanceStatus;
  per_installment_amount: number | null;
  total_payable: number | null;
  repayment_schedule: RepaymentInstallment[] | null;
  hr_note: string | null;
  partner_note: string | null;
  disbursed_at: string | null;
  created_at: string;
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export function useRequestSalaryAdvance(
  options?: Parameters<typeof useMutation>[2],
) {
  const { mutate, mutateAsync, isLoading, isError, error, data } = useMutation<
    APIResponse<SalaryAdvanceRequest>,
    APIError,
    { amount: number; reason?: string; repayment_months: number }
  >(
    (variables) =>
      post(API_URL_V1 + '/advance-loan/request', variables, {
        headers: authHeaders(),
      }),
    options as any,
  );

  return { requestAdvance: mutate, requestAdvanceAsync: mutateAsync, isLoading, isError, error, data };
}

export function useMyAdvanceRequests() {
  return useQuery<APIResponse<SalaryAdvanceRequest[]>, APIError>(
    ['my-advance-requests'],
    () =>
      get(API_URL_V1 + '/advance-loan/my-requests', {
        headers: authHeaders(),
      }),
    { refetchOnWindowFocus: false },
  );
}
